import { parseDbTimestamp } from "@repo/shared";
import type { IdentityVerification } from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./verified-id-dialog.i18n";

type VerifiedIdDialogProps = {
  open: boolean;
  onClose: () => void;
  verifications: IdentityVerification[];
};

/**
 * QL-e — how and when this identity was verified.
 *
 * Lists EVERY verification held, newest first. The newest is the standing one
 * and is the only one that drives the icon; older ones are shown as earlier
 * checks.
 *
 * Nothing is hidden, including a verification that has gone stale. Hiding it
 * would erase the fact that a check was once performed, which is the audit
 * trail; presenting it as current would be a lie. So it is labelled NO LONGER
 * VALID — the same treatment QL-21 gives a stale compliance verdict, which the
 * review list still shows with who decided and when.
 */
export function VerifiedIdDialog({ open, onClose, verifications }: VerifiedIdDialogProps) {
  const t = useT(T);
  const { formatDateTime } = useRegionalFormat();
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {verifications.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.empty}</p>
          ) : (
            /* A divided list, NOT bordered cards: one entry is the common
               case, and a box inside the dialog's own box is a frame around
               nothing. Dividers only appear once there is something to
               separate. */
            <ul className="divide-y">
              {verifications.map((entry) => {
                // Only the standing entry can be "current" — an older entry
                // whose hash still matches is history, not a standing
                // verification. The server decides which is standing: the newest
                // for one person, the newest per person for several (QL-654).
                const isStanding = entry.isStanding;
                const status = isStanding
                  ? entry.isCurrent
                    ? { label: t.current, variant: "success" as const }
                    : { label: t.noLongerValid, variant: "warning" as const }
                  : { label: t.earlierCheck, variant: "secondary" as const };

                return (
                  <li key={entry.id} className="space-y-1 py-3 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {entry.source === "idura_signing" ? t.eidSigning : t.documentCheck}
                      </span>
                      <div className="flex items-center gap-2">
                        {entry.isTestMode && <Badge variant="secondary">{t.testMode}</Badge>}
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      {[
                        formatDateTime(parseDbTimestamp(entry.verifiedAt)),
                        // The "how": eID provider, or document type plus the
                        // issuing country — which is NOT the subject's own
                        // country, since a resident of one state can hold
                        // another's passport.
                        entry.method,
                        entry.documentCountry,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>

                    {/* WHO was verified. For a Company or Group this is the
                        representative, and attributing their verification to
                        the company without saying so is the whole failure this
                        line prevents. */}
                    <p className="text-xs text-muted-foreground">
                      {entry.subjectKind === "representative"
                        ? t.verifiedVia(entry.subjectName ?? "—")
                        : t.verifiedSelf}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
